<template>
  <div class="edit-event-dialog">
    <v-dialog v-model="open" width="400">
      <v-card class="event-form">
        <v-card-title class="p-3" color="primary">
          <v-icon class="mr-3" @click="close">
            mdi-close
          </v-icon>
          {{$t('TestEmailTemplateDialog.TestEmailTemplate')}}
        </v-card-title>
        <v-card-text class="pt-4">
          <validation-observer ref="observer" v-slot="{ invalid }">
            <validation-provider v-slot="{ errors }" name="email" rules="email|required" :error-messages="errors">
              <v-text-field class="mb-4" v-model="placeholdersModel.to" :counter="40" :label="$t('TestEmailTemplateDialog.ToEmail')+'*'" required
                :error-messages="errors">
              </v-text-field>
            </validation-provider>
            <div v-for="ph in placeholdersProperties" :key="ph.name">
              <v-text-field v-model="ph.value" :label="ph.name"></v-text-field>
            </div>
            <p v-if="!isSendEmail" class="error--text theme--light">
              {{$t('TestEmailTemplateDialog.FamilyMemberUnsubscribeFromEmails')}}.
            </p>
            <v-row>
              <v-col sm="12" md="12" align="center">
                <v-btn @click="cancel">Cancel</v-btn>
                <v-btn class="primary ml-4" type="submit" @click="send" :disabled="invalid || submited">{{$t('TestEmailTemplateDialog.Send')}}</v-btn>
              </v-col>
            </v-row>
          </validation-observer>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as firebase from "firebase/app";
import "@/plugins/vee-validate";
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import {DefaultEmailTemplateType} from "@/enums/DefaultEmailTemplateType";
import {FamilyHubAppStatus} from "@/enums/FamilyHubAppStatus";
import juice from "juice";
import {EditorStyles} from "@/components/editor/EditorStyles";
import { v4 as uuidv4 } from "uuid";

export default {
  components: {
    ValidationProvider,
    ValidationObserver
  },
  props: {
    emailTemplate: {
      type: Object,
      default: () => ({})
    },
    isCustomEmailTemplate: {
      type: Boolean,
      default: () => ({})
    },

    value: Boolean
  },
  data() {
    return {
      placeholdersModel: { to: '' },
      placeholdersProperties: [],
      submited: false,
      isSendEmail: true,
      content: ""
    };
  },

  computed: {
    open: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },

  },

  created() {
    this.content = juice.inlineContent(this.emailTemplate.html || '', EditorStyles);

    //get the placeholders from the email template
    const placeholders = this.emailTemplate.html.match(/{{(.*?)}}/g).flat(1).filter((value, index, self) => value && self.indexOf(value) === index)

    //map the placeholders to the placeholdersProperties array
    placeholders.forEach(placeholder => {
      this.placeholdersProperties.push({
        name: placeholder,
        value: ''
      })
    });

  },

  methods: {
    close() {
      this.open = false
    },
    cancel() {
      this.close()
    },
    async send() {

      if (!this.submited) {
        this.submited = true

        for(let i = 0; i < this.placeholdersProperties.length; i++) {
          this.content = this.content.replace(this.placeholdersProperties[i].name, this.placeholdersProperties[i].value)
          await this.rulesForPasswordLink(this.placeholdersProperties[i]);
          await this.rulesForResidentName(this.placeholdersProperties[i]);
          await this.rulesForFamilyMemberName(this.placeholdersProperties[i]);
          await this.rulesForFamilyMemberEmail(this.placeholdersProperties[i]);
          await this.rulesForFamilyMemberImage(this.placeholdersProperties[i]);
        }

        await this.checkFamilyMemberOnUnsubscribe();

        if(this.isSendEmail) {
          await firebase
            .firestore()
            .collection('email')
            .add({
              from: "support@getreshub.com",
              to: this.placeholdersModel.to,
              facilityId: this.$store.state.authModule.currentFacility,
              message: {
                subject: this.emailTemplate.subject,
                html: this.content
              }
            })
            .catch((error) => {
              this.$toast.open({
                message: this.$i18n.messages[this.$i18n.locale]['TestEmailTemplateDialog.ErrorSendingTestEmail'],
                type: "error"
              });
              console.error(this.$i18n.messages[this.$i18n.locale]['TestEmailTemplateDialog.ErrorSendingTestEmail'], error);
            }).finally(() => {
              this.close()
            }).then(() => {
              this.submited = false
              this.$toast.open({
                message: this.$i18n.messages[this.$i18n.locale]['TestEmailTemplateDialog.TestEmailSent'],
                type: "success"
              });
            });
        }
      }
    },
    async checkFamilyMemberOnUnsubscribe() {
      await firebase
        .firestore()
        .collection('familyHubUsers')
        .where("facilityId", "==", this.$store.state.authModule.currentFacility)
        .where("email", "==", this.placeholdersModel.to)
        .get()
        .then(familyMember => {
          familyMember.docs.forEach((doc) => {
            const family = doc.data();
            if(family.isUnsubscribedFromEmails && this.emailTemplate.type !== DefaultEmailTemplateType.BroadcastEmail){
              this.isSendEmail = false;
            }
          })
        });
    },
    async rulesForResidentName(placeholder) {
      if(placeholder.name.includes("residentName")) {
        const to = this.placeholdersModel.to;

        await firebase.firestore().collection("familyHubUsers")
          .where("facilityId", "==", this.$store.state.authModule.currentFacility)
          .where("email", "==", to)
          .get()
          .then(async (querySnapshot) => {
            await querySnapshot.forEach((doc) => {
              const residents = doc.data().residents;
              let residentsNames = "";

              residents.forEach((resident, index) => {
                if(residents.length-1 > index) {
                  residentsNames += `${resident.name} and `
                } else {
                  residentsNames += `${resident.name}`
                }
              })

              this.content = this.content.replace(placeholder.value, `${residentsNames} (${placeholder.value})`)
            })
          })
      }
    },
    async rulesForFamilyMemberName(placeholder) {
      if(placeholder.name.includes("familyMemberName") || placeholder.name.includes("familyUserName")) {
        const to = this.placeholdersModel.to;
        await firebase.firestore().collection("familyHubUsers")
          .where("facilityId", "==", this.$store.state.authModule.currentFacility)
          .where("email", "==", to)
          .get()
          .then(async (querySnapshot) => {
            await querySnapshot.forEach((doc) => {
              const familyMember = doc.data();
              this.content = this.content.replace(placeholder.value, `${familyMember.name} (${placeholder.value})`)
            })
          })
      }
    },
    async rulesForFamilyMemberEmail(placeholder) {
      if(placeholder.name.includes("familyMemberEmail") || placeholder.name.includes("familyUserEmail")) {
        const to = this.placeholdersModel.to;
        await firebase.firestore().collection("familyHubUsers")
          .where("facilityId", "==", this.$store.state.authModule.currentFacility)
          .where("email", "==", to)
          .get()
          .then(async (querySnapshot) => {
            await querySnapshot.forEach((doc) => {
              const familyMember = doc.data();
              this.content = this.content.replace(placeholder.value, `${familyMember.email} (${placeholder.value})`)
            })
          })
      }
    },
    async rulesForFamilyMemberImage(placeholder) {
      if(placeholder.name.includes("familyMemberImageUrl") || placeholder.name.includes("familyUserImageUrl")) {
        const to = this.placeholdersModel.to;
        await firebase.firestore().collection("familyHubUsers")
          .where("facilityId", "==", this.$store.state.authModule.currentFacility)
          .where("email", "==", to)
          .get()
          .then(async (querySnapshot) => {
            await querySnapshot.forEach((doc) => {
              const familyMember = doc.data();
              this.content = this.content.replace(placeholder.value, `<img src="${familyMember.email}"> (${placeholder.value})`)
            })
          })
      }
    },
    async rulesForPasswordLink(placeholder) {
      const placeholderPassword = placeholder.name.replace(/\s/g,'');

      if(placeholderPassword === "{{setPasswordLink}}") {
        const passwordToken = uuidv4();
        const baseUrl = this.$store.state.facilityModule.selectedFacility.baseUrl;
        const to = this.placeholdersModel.to;

        firebase.firestore().collection("familyHubUsers")
          .where("facilityId", "==", this.$store.state.authModule.currentFacility)
          .where("email", "==", to).get()
          .then(async (querySnapshot) => {
            await querySnapshot.forEach((doc) => {
              const familyMember = doc.data();

              if(!familyMember.setPasswordToken) {
                console.log("SEND INVITE")
                doc.ref.update({
                  setPasswordToken: passwordToken,
                  invitationSentDateTime: firebase.firestore.Timestamp.fromDate(new Date()),
                }).then(() => {
                  const setPasswordLink = `https://${baseUrl}/familyhub/set-password/${passwordToken}`;
                  firebase
                    .firestore()
                    .collection('email')
                    .add({
                      from: "support@getreshub.com",
                      to: to,
                      facilityId: this.$store.state.authModule.currentFacility,
                      message: {
                        subject: "Create new password",
                        html: `<a href="${setPasswordLink}">Create New Password</a>`
                      }
                    })
                })
              }

              if(familyMember.setPasswordToken && familyMember.appStatus !== FamilyHubAppStatus.Activated) {
                console.log("SET PASSWORD")
                doc.ref.update({
                  setPasswordToken: passwordToken
                }).then(() => {
                  const setPasswordLink = `https://${baseUrl}/familyhub/set-password/${passwordToken}`;
                  firebase
                    .firestore()
                    .collection('email')
                    .add({
                      from: "support@getreshub.com",
                      to: to,
                      facilityId: this.$store.state.authModule.currentFacility,
                      message: {
                        subject: "Set new password",
                        html: `<a href="${setPasswordLink}">Set New Password</a>`
                      }
                    })
                })
              }

              if(familyMember.setPasswordToken && familyMember.appStatus === FamilyHubAppStatus.Activated) {
                console.log("RESET PASSWORD")
                doc.ref.update({
                  resetPasswordToken: passwordToken
                }).then(() => {
                  const resetPasswordLink = `https://${baseUrl}/familyhub/reset-password/${passwordToken}`;
                  firebase
                    .firestore()
                    .collection('email')
                    .add({
                      from: "support@getreshub.com",
                      to: to,
                      facilityId: this.$store.state.authModule.currentFacility,
                      message: {
                        subject: "Reset password",
                        html: `<a href="${resetPasswordLink}">Reset Password</a>`
                      }
                    })
                })
              }

            })
          })
      }
    }
  }
};
</script>

<style scoped>
</style>
